// Fraction Kitchen generator. Pure and seeded. Every problem carries its answer as a
// reduced fraction {n, d}; choice-based problems carry exactly one correct choice.

import { int, pick, shuffle, type Rng } from '@kit/rng';

export interface Frac { n: number; d: number }

export function gcd(a: number, b: number): number { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a || 1; }
export function reduce(f: Frac): Frac { const g = gcd(f.n, f.d); return { n: f.n / g, d: f.d / g }; }
export function value(f: Frac): number { return f.n / f.d; }
export function fracEq(a: Frac, b: Frac): boolean { return a.n * b.d === b.n * a.d; }
export function add(a: Frac, b: Frac): Frac { return reduce({ n: a.n * b.d + b.n * a.d, d: a.d * b.d }); }
export function lcm(a: number, b: number): number { return (a * b) / gcd(a, b); }

/** "3/4", "1 1/2", "2" */
export function fmt(f: Frac): string {
  const r = reduce(f);
  if (r.d === 1) return String(r.n);
  if (r.n > r.d) { const w = Math.floor(r.n / r.d); const rem = r.n - w * r.d; return `${w} ${rem}/${r.d}`; }
  return `${r.n}/${r.d}`;
}
export function parse(s: string): Frac {
  const m = s.trim().match(/^(\d+)(?:\s+(\d+)\/(\d+))?$|^(\d+)\/(\d+)$/);
  if (!m) return { n: 0, d: 1 };
  if (m[4] !== undefined) return reduce({ n: Number(m[4]), d: Number(m[5]) });
  const w = Number(m[1]);
  if (m[2] !== undefined) { const d = Number(m[3]); return reduce({ n: w * d + Number(m[2]), d }); }
  return { n: w, d: 1 };
}

export type Shape = 'pizza' | 'bar';

export type FracProblem =
  | { kind: 'shade'; shape: Shape; d: number; target: Frac; answer: Frac; prompt: string }
  | { kind: 'name'; shape: Shape; d: number; shaded: number; choices: string[]; answer: Frac; prompt: string }
  | { kind: 'compare'; a: Frac; b: Frac; answer: Frac; bigger: 'a' | 'b'; prompt: string }
  | { kind: 'equivalent'; shape: Shape; d: number; target: Frac; answer: Frac; prompt: string }
  | { kind: 'addLike'; a: Frac; b: Frac; choices: string[]; answer: Frac; prompt: string }
  | { kind: 'numberLine'; max: 1 | 2; target: Frac; answer: Frac; prompt: string }
  | { kind: 'mixed'; whole: number; part: Frac; choices: string[]; answer: Frac; prompt: string }
  | { kind: 'addUnlike'; a: Frac; b: Frac; common: number; choices: string[]; answer: Frac; prompt: string }
  | { kind: 'ofSet'; frac: Frac; total: number; answer: Frac; prompt: string }
  | { kind: 'times'; frac: Frac; whole: number; choices: string[]; answer: Frac; prompt: string };

function distinctChoices(rng: Rng, answer: Frac, pool: Frac[], count = 4): string[] {
  const out: string[] = [fmt(answer)];
  const cands = shuffle(rng, pool.filter(f => !fracEq(f, answer) && f.n >= 0));
  for (const f of cands) { const s = fmt(f); if (!out.includes(s)) out.push(s); if (out.length >= count) break; }
  // pad if the pool ran dry
  let k = 1;
  while (out.length < count && k < 50) { const f = reduce({ n: answer.n + k, d: answer.d }); const s = fmt(f); if (!out.includes(s)) out.push(s); k++; }
  return shuffle(rng, out);
}

export function makeFraction(level: number, rng: Rng): FracProblem {
  const L = Math.max(1, Math.min(10, level));
  if (L <= 2) {
    const d = pick(rng, L === 1 ? [2, 4] : [2, 3, 4]);
    const n = int(rng, 1, d - 1);
    const target = { n, d };
    return { kind: 'shade', shape: 'pizza', d, target, answer: reduce(target), prompt: `Shade ${fmt(target)} of the pizza, then tap Check.` };
  }
  if (L <= 4) {
    if (rng() < 0.5) {
      const shape: Shape = rng() < 0.5 ? 'pizza' : 'bar';
      const d = int(rng, 3, 8), shaded = int(rng, 1, d - 1);
      const answer = reduce({ n: shaded, d });
      const pool: Frac[] = [];
      for (let k = 1; k < d; k++) pool.push({ n: k, d });
      for (const dd of [d - 1, d + 1]) if (dd >= 2) pool.push({ n: Math.min(shaded, dd - 1), d: dd });
      return { kind: 'name', shape, d, shaded, choices: distinctChoices(rng, { n: shaded, d }, pool), answer, prompt: 'What fraction is shaded?' };
    }
    const d = int(rng, 3, 8), n = int(rng, 1, d - 1);
    return { kind: 'shade', shape: 'bar', d, target: { n, d }, answer: reduce({ n, d }), prompt: `The bar is cut into ${d} equal pieces. Shade ${n}/${d}, then tap Check.` };
  }
  if (L <= 6) {
    if (rng() < 0.5) {
      let a: Frac, b: Frac;
      do { a = { n: int(rng, 1, 7), d: int(rng, 2, 8) }; b = { n: int(rng, 1, 7), d: int(rng, 2, 8) }; }
      while (a.n >= a.d || b.n >= b.d || fracEq(a, b));
      const bigger = value(a) > value(b) ? 'a' : 'b';
      const ans = bigger === 'a' ? a : b;
      return { kind: 'compare', a, b, bigger, answer: reduce(ans), prompt: 'Tap the bigger fraction.' };
    }
    const target = pick(rng, [{ n: 1, d: 2 }, { n: 1, d: 3 }, { n: 2, d: 3 }, { n: 1, d: 4 }, { n: 3, d: 4 }]);
    const mult = pick(rng, [2, 3].filter(m => target.d * m <= 12));
    const d = target.d * mult;
    return { kind: 'equivalent', shape: 'bar', d, target, answer: reduce(target), prompt: `This bar has ${d} pieces. Shade the same amount as ${fmt(target)}, then tap Check.` };
  }
  if (L <= 8) {
    const r = rng();
    if (r < 0.4) {
      const d = int(rng, 3, 8);
      const an = int(rng, 1, d - 2), bn = int(rng, 1, d - 1 - an);
      const a = { n: an, d }, b = { n: bn, d };
      const answer = add(a, b);
      const pool: Frac[] = [{ n: an + bn, d: d * 2 }, { n: an * bn, d }, { n: an + bn + 1, d }, { n: Math.max(1, an + bn - 1), d }, { n: an + bn, d: d + 1 }];
      return { kind: 'addLike', a, b, choices: distinctChoices(rng, answer, pool), answer, prompt: `${fmt(a)} + ${fmt(b)} = ? Count the shaded slices.` };
    }
    if (r < 0.7) {
      const max: 1 | 2 = L === 8 && rng() < 0.5 ? 2 : 1;
      const d = pick(rng, [2, 3, 4, 6, 8]);
      const n = int(rng, 1, d * max - 1);
      const target = { n, d };
      return { kind: 'numberLine', max, target, answer: reduce(target), prompt: `Drag the marker to ${fmt(target)} on the number line, then tap Check.` };
    }
    const whole = int(rng, 1, 2);
    const d = pick(rng, [2, 3, 4]);
    const part = { n: int(rng, 1, d - 1), d };
    const answer = reduce({ n: whole * d + part.n, d });
    const pool: Frac[] = [{ n: whole * d + part.n + 1, d }, { n: (whole - 1) * d + part.n, d }, { n: whole * d + part.n, d: d * 2 }, { n: (whole + 1) * d + part.n, d }, { n: whole, d: 1 }];
    return { kind: 'mixed', whole, part, choices: distinctChoices(rng, answer, pool), answer, prompt: 'How much pizza is there altogether?' };
  }
  const r = rng();
  if (r < 0.4) {
    let a: Frac, b: Frac;
    do { a = { n: 1, d: int(rng, 2, 6) }; b = { n: int(rng, 1, 3), d: int(rng, 2, 6) }; }
    while (a.d === b.d || b.n >= b.d || lcm(a.d, b.d) > 12 || value(a) + value(b) > 1);
    const common = lcm(a.d, b.d);
    const answer = add(a, b);
    const pool: Frac[] = [{ n: a.n + b.n, d: a.d + b.d }, { n: a.n + b.n, d: common }, { n: answer.n + 1, d: answer.d }, { n: a.n * b.d + b.n * a.d + 1, d: common }, { n: Math.max(1, answer.n - 1), d: answer.d }];
    return { kind: 'addUnlike', a, b, common, choices: distinctChoices(rng, answer, pool), answer, prompt: `${fmt(a)} + ${fmt(b)}: first pick a cut that works for both bars, then add.` };
  }
  if (r < 0.7) {
    const d = pick(rng, [2, 3, 4, 5, 6]);
    const total = d * int(rng, 1, Math.floor(12 / d));
    const frac = { n: int(rng, 1, d - 1), d };
    const answer = { n: (total / d) * frac.n, d: 1 };
    return { kind: 'ofSet', frac, total, answer, prompt: `Tap ${fmt(frac)} of the ${total} cookies, then tap Check.` };
  }
  const d = pick(rng, [2, 3, 4, 5, 6, 8]);
  const frac = { n: int(rng, 1, d - 1), d };
  const whole = int(rng, 2, 6);
  const answer = reduce({ n: frac.n * whole, d });
  const pool: Frac[] = [{ n: frac.n * whole, d: d * whole }, { n: frac.n + whole, d }, { n: frac.n * whole + 1, d }, { n: Math.max(1, frac.n * whole - 1), d }, { n: frac.n, d: d * whole }];
  return { kind: 'times', frac, whole, choices: distinctChoices(rng, answer, pool), answer, prompt: `${fmt(frac)} × ${whole} = ? Think of ${whole} groups of ${fmt(frac)}.` };
}
